import React from 'react';
import './eventsFilter.css';
import moment from 'moment';
import { classnames } from '../../../../utils/classnames';
import { useTranslation } from 'react-i18next';

import DatePicker from '../../ui/date-picker/datePicker';
import Icon from '../../ui/icon/icon';
import Input from '../../ui/inputs/inputs';
import Button, { ButtonGroup } from '../../ui/button/button';
import { formatLocale } from '../../../ui/date/date';

export default function EventsFilter({ onChange = () => {} }) {
    const { t, i18n } = useTranslation();
    const defaultForm = {
        search: '',
        startDate: null,
        endDate: null
    };

    const [filtersForm, setFiltersForm] = React.useState(defaultForm);
    const [lastSavedForm, setLastSavedForm] = React.useState(defaultForm);

    const [isOpen, setIsOpen] = React.useState(false);

    const setFilter = React.useCallback((key, value) => {
        const newForm = { ...filtersForm };
        newForm[key] = value;
        setFiltersForm(newForm);
    }, [filtersForm]);

    const _locale = React.useMemo(() => formatLocale(i18n.language), [i18n.language]);

    const dateFormat = React.useMemo(() => {
        moment.locale(_locale);
        return moment.localeData().longDateFormat('L');
    }, [_locale]);

    const isoFormat = "YYYY-MM-DD[T]HH:mm:ssZ";
    const isoToLocalDate = React.useCallback((isoDateString) => {
        if (!isoDateString) return null;
        const isoDate = moment(isoDateString, isoFormat);
        return isoDate.format(dateFormat);
    }, [dateFormat]);

    // concat start and end dates (use locale format as placeholder if one is missing)
    const dateRange = React.useMemo(() => (filtersForm.startDate || filtersForm.endDate) &&
     `${isoToLocalDate(filtersForm.startDate) || dateFormat} - ${isoToLocalDate(filtersForm.endDate) || dateFormat}`, [filtersForm, dateFormat, isoToLocalDate]);

    const errorRange = React.useMemo(() => 
        filtersForm.startDate && filtersForm.endDate && new Date(filtersForm.startDate) > new Date(filtersForm.endDate) ?
            t('widget.input.filter.error_range') :
            ''
    , [t, filtersForm]);

    const onSubmit = React.useCallback(() => {
        onChange(filtersForm);
        setLastSavedForm(filtersForm);
        setIsOpen(false);
    }, [filtersForm, onChange]);

    const onReset = React.useCallback(() => {
        setFiltersForm(lastSavedForm);
        setIsOpen(false);
    }, [lastSavedForm]);

    const onClear = React.useCallback(() => {
        setFiltersForm(defaultForm);
        setLastSavedForm(defaultForm);
        onChange(defaultForm);
    }, [onChange]);

    const getDateIso = React.useCallback((newDate, endDay = false) => {
        let isoDate = null;
        if (newDate) {
            isoDate = moment(newDate)
                ?.set({ h: endDay ? 23 : 0, m: endDay ? 59 : 0, s: endDay ? 59 : 0 })
                .format(isoFormat);
        }
        return isoDate;
    }, []);

    return (
        <div className={classnames('neo-event-filter')}>
            {!isOpen && (
                <div className='neo-trigger-container'>
                    <Input
                        className={classnames('neo-event-filter-trigger', ((filtersForm.search || dateRange) && 'value'))}
                        onClick={() => setIsOpen(true)}
                        icon="search"
                        value=""
                        onKeyDown={e => { if (['Enter', 'Space'].includes(e.key)) setIsOpen(true) }}
                        label={
                            <div className='neo-filters-label'>
                                {filtersForm.search && <div className='neo-input-label'>
                                    {filtersForm.search}
                                </div>}
                                {dateRange && <div className='neo-input-description'>
                                    {dateRange}
                                </div>}
                            </div>
                        }
                        placeholder={!filtersForm.search && !dateRange ? t('widget.input.filter.search') : " "}
                    />
                    {Boolean(filtersForm.search || dateRange) && <div className='neo-clear-button' onClick={onClear}>
                        <Icon name='cross' size='large'/>
                    </div>}
                </div>
            )}
            {isOpen && (
                <div className='neo-card-container'>
                    <div className='neo-filter-cross' onClick={onReset} tabIndex="0" onKeyDown={e => { if (['Enter', 'Space'].includes(e.key)) onReset() }}>
                        <Icon name='cross' size='large'/>
                    </div>
                    <div className='neo-margin-input'>
                        <Input
                            as="button"
                            className={classnames('neo-event-filter-trigger', 'open')}
                            icon="search"
                            value={filtersForm.search}
                            placeholder={t('widget.input.filter.search')}
                            onChange={(e) => setFilter('search', e.target.value)}
                        />
                        <DatePicker
                            enableOutsideDays={false}
                            maxDate={filtersForm.endDate}
                            locale={i18n.language}
                            label={t('widget.input.filter.start_date')}
                            value={filtersForm.startDate}
                            onChange={(date) => setFilter('startDate', getDateIso(date))}
                        />
                        <DatePicker
                            enableOutsideDays={false}
                            minDate={filtersForm.startDate}
                            locale={i18n.language}
                            label={t('widget.input.filter.end_date')}
                            value={filtersForm.endDate}
                            onChange={(date) => setFilter('endDate', getDateIso(date, true))}
                            error={errorRange}
                        />
                    </div>
                    <ButtonGroup>
                        <Button label={t('widget.button.cancel.default_label')} onClick={onReset} type="secondary" />
                        <Button label={t('widget.actions.search')} onClick={onSubmit} disabled={errorRange} />
                    </ButtonGroup>
                </div>
            )}
        </div>
    )
}